import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, Button, Input, Alert } from '../components/ui';
import { FiMail, FiCheckCircle, FiXCircle, FiRefreshCw } from 'react-icons/fi';
import { supabase } from '../config/supabase';

function VerifyEmail() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [verifyingLink, setVerifyingLink] = useState(false);

    useEffect(() => {
        // Check if this is a magic link callback (has token and type in URL)
        const token = searchParams.get('token');
        const type = searchParams.get('type');
        
        // Handle magic link verification
        if (token && type === 'email') {
            handleMagicLinkVerification(token);
            return;
        }
        
        // Get email from URL params or localStorage
        const emailParam = searchParams.get('email');
        const storedEmail = localStorage.getItem('pendingVerificationEmail');
        
        if (emailParam) {
            setEmail(emailParam);
            localStorage.setItem('pendingVerificationEmail', emailParam);
        } else if (storedEmail) {
            setEmail(storedEmail);
        }

        // Automatically request verification code when page loads
        if (emailParam || storedEmail) {
            const requestCode = async () => {
                try {
                    const emailToUse = emailParam || storedEmail;
                    const response = await fetch('/api/auth/request-verification-code', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            email: emailToUse
                        }),
                    });
                    const data = await response.json();
                    if (data.success) {
                        console.log('Verification code requested successfully');
                    }
                } catch (error) {
                    console.error('Failed to request verification code:', error);
                }
            };
            requestCode();
        }

        // Start countdown for resend (60 seconds)
        setCountdown(60);
        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [searchParams]);

    // Handle magic link verification (when user clicks link from email)
    const handleMagicLinkVerification = async (token) => {
        setVerifyingLink(true);
        setError('');
        
        try {
            console.log('🔗 Verifying magic link token...');
            
            // Try different token formats that Supabase might use
            let verificationResult = null;
            
            // Method 1: Try token_hash
            try {
                const { data, error } = await supabase.auth.verifyOtp({
                    token_hash: token,
                    type: 'email'
                });
                if (!error && data?.user) {
                    verificationResult = { success: true, user: data.user };
                }
            } catch (e) {
                console.log('Token hash method failed, trying token method...');
            }
            
            // Method 2: Try token directly
            if (!verificationResult) {
                try {
                    const { data, error } = await supabase.auth.verifyOtp({
                        token: token,
                        type: 'email'
                    });
                    if (!error && data?.user) {
                        verificationResult = { success: true, user: data.user };
                    }
                } catch (e) {
                    console.log('Token method failed, trying signIn with session...');
                }
            }
            
            // Method 3: Check if Supabase session exists (magic link might have already verified)
            if (!verificationResult) {
                const { data: { session } } = await supabase.auth.getSession();
                if (session?.user) {
                    verificationResult = { success: true, user: session.user };
                }
            }
            
            if (verificationResult?.success) {
                console.log('✅ Magic link verified successfully');
                setSuccess(true);
                localStorage.removeItem('pendingVerificationEmail');
                
                // Update email confirmation status via backend
                try {
                    await fetch('/api/auth/verify-otp', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            email: verificationResult.user.email,
                            token: token,
                            type: 'email'
                        }),
                    });
                } catch (e) {
                    console.warn('Failed to sync verification status:', e);
                }
                
                setTimeout(() => {
                    navigate('/login', { 
                        state: { 
                            message: 'Email verified successfully! You can now log in.' 
                        } 
                    });
                }, 2000);
            } else {
                console.error('❌ Magic link verification failed');
                setError('Invalid or expired verification link. Please request a new verification code from the login page.');
                setVerifyingLink(false);
            }
        } catch (error) {
            console.error('Magic link verification error:', error);
            setError('Failed to verify email link. Please try requesting a new verification code.');
            setVerifyingLink(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!email || !code) {
            setError('Please enter your email and verification code');
            setLoading(false);
            return;
        }

        try {
            const response = await fetch('/api/auth/verify-otp', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: email.trim(),
                    token: code.trim(),
                    type: 'email'
                }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSuccess(true);
                localStorage.removeItem('pendingVerificationEmail');
                
                // Redirect to login after 2 seconds
                setTimeout(() => {
                    navigate('/login', { 
                        state: { 
                            message: 'Email verified successfully! You can now log in.' 
                        } 
                    });
                }, 2000);
            } else {
                setError(data.message || 'Invalid verification code. Please try again.');
            }
        } catch (error) {
            console.error('Verification error:', error);
            setError('An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        if (countdown > 0 || !email) return;

        setResending(true);
        setError('');

        try {
            const response = await fetch('/api/auth/resend-otp', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: email.trim(),
                    type: 'email'
                }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setError('');
                alert('Verification code resent successfully! Please check your email.');
                setCountdown(60); // Reset countdown
                
                // Restart countdown
                const timer = setInterval(() => {
                    setCountdown(prev => {
                        if (prev <= 1) {
                            clearInterval(timer);
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);
            } else {
                setError(data.message || 'Failed to resend verification code. Please try again.');
            }
        } catch (error) {
            console.error('Resend error:', error);
            setError('An error occurred while resending code. Please try again.');
        } finally {
            setResending(false);
        }
    };

    const handleCodeChange = (e) => {
        const value = e.target.value.replace(/\D/g, ''); // Only allow digits
        // Accept both 6 and 8 digit codes (Supabase may send either format)
        if (value.length <= 8) {
            setCode(value);
            setError(''); // Clear error when user types
        }
    };

    if (verifyingLink) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center py-12 px-4">
                <Card className="max-w-md w-full">
                    <Card.Content className="text-center">
                        <div className="mb-4 flex justify-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                        </div>
                        <h2 className="text-2xl font-serif font-bold text-primary-900 dark:text-white mb-2">
                            Verifying Email...
                        </h2>
                        <p className="text-primary-600 dark:text-gray-300">
                            Please wait while we verify your email link.
                        </p>
                    </Card.Content>
                </Card>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center py-12 px-4">
                <Card className="max-w-md w-full">
                    <Card.Content className="text-center">
                        <div className="mb-4 flex justify-center">
                            <FiCheckCircle className="text-6xl text-success-500" />
                        </div>
                        <h2 className="text-2xl font-serif font-bold text-primary-900 dark:text-white mb-2">
                            Email Verified!
                        </h2>
                        <p className="text-primary-600 dark:text-gray-300 mb-6">
                            Your email has been successfully verified. Redirecting to login...
                        </p>
                        <Button
                            variant="primary"
                            onClick={() => navigate('/login')}
                        >
                            Go to Login
                        </Button>
                    </Card.Content>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <Card className="max-w-md w-full">
                <Card.Header className="text-center">
                    <div className="mb-4 flex justify-center">
                        <FiMail className="text-5xl text-primary-500" />
                    </div>
                    <Card.Title className="text-3xl font-elegant text-primary-900 dark:text-white">
                        Verify Your Email
                    </Card.Title>
                    <Card.Description className="font-literary text-primary-600 dark:text-gray-300 mt-2">
                        We've sent a verification code to your email address. Please enter it below to verify your account.
                    </Card.Description>
                </Card.Header>
                <Card.Content>
                    <form onSubmit={handleVerify} className="space-y-6">
                        <div>
                            <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-2">
                                Email Address
                            </label>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your.email@example.com"
                                icon={FiMail}
                                required
                                disabled={!!searchParams.get('email')}
                                className="bg-gray-50 dark:bg-gray-800"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-serif font-medium text-primary-700 dark:text-gray-300 mb-2">
                                Verification Code
                            </label>
                            <Input
                                type="text"
                                value={code}
                                onChange={handleCodeChange}
                                placeholder="Enter verification code (6-8 digits)"
                                maxLength={8}
                                required
                                className="text-center text-2xl tracking-widest font-mono"
                                autoFocus
                            />
                            <p className="mt-2 text-xs text-primary-500 dark:text-gray-400">
                                Enter the verification code sent to your email (6-8 digits)
                            </p>
                        </div>

                        {error && (
                            <Alert type="error">
                                {error}
                            </Alert>
                        )}

                        <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            className="w-full"
                            disabled={loading || !code || code.length < 6 || code.length > 8}
                        >
                            {loading ? 'Verifying...' : 'Verify Email'}
                        </Button>

                        <div className="text-center">
                            <p className="text-sm text-primary-600 dark:text-gray-400 mb-2">
                                Didn't receive the code?
                            </p>
                            <button
                                type="button"
                                onClick={handleResendCode}
                                disabled={resending || countdown > 0}
                                className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mx-auto"
                            >
                                <FiRefreshCw className={resending ? 'animate-spin' : ''} />
                                {countdown > 0 
                                    ? `Resend code in ${countdown}s` 
                                    : resending 
                                        ? 'Sending...' 
                                        : 'Resend Verification Code'}
                            </button>
                        </div>
                    </form>
                </Card.Content>
                <Card.Footer className="text-center">
                    <p className="font-literary text-primary-600 dark:text-gray-400">
                        Already verified?{' '}
                        <Link to="/login" className="font-serif text-primary-700 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 transition-colors">
                            Sign in here
                        </Link>
                    </p>
                </Card.Footer>
            </Card>
        </div>
    );
}

export default VerifyEmail;
